import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import styles from "./CatalogPage.module.css";
import Carousel from "../../components/Carousel/Carousel";
import { ReactComponent as HeartIcon } from "../../assets/transperentHeart.svg";
import { ReactComponent as HeartNavIcon } from "../../assets/heart.svg";
import { ReactComponent as CatalogNavIcon } from "../../assets/catalog.svg";
import { ReactComponent as BucketNavIcon } from "../../assets/bucket.svg";
import { ReactComponent as SupportNavIcon } from "../../assets/support.svg";
import {
  getBanners,
  getCatalog,
  getCategoriesTree,
} from "../../requests/catalog/catalog";
import type {
  CatalogCategory,
  CatalogItem,
  CatalogSubCategory,
} from "../../types/catalog";
import {
  getFavorites,
  addToFavorites,
  removeFromFavorites,
} from "../../requests/favorites/favorites";
import { addToCart, searchProducts } from "../../requests/cart/cart";
import { useNotificationStore } from "../../store/NotificationStore";
import { useCartStore } from "../../store/useCartStore";
import { BackButton } from "../../components/BackButton/BackButton";
import SupportQuick from "../../components/SupportQuick/SupportQuick";
import SearchQuick from "../../components/SearchQuick/SearchQuick";
import Loader from "../../components/Loader/Loader";
import { useLocation, useNavigate } from "react-router-dom";
import copyButtonIcon from "../../assets/copyButton.png";
import { useNavMenuStore } from "../../store/useNavMenuStore";
import type { MouseEvent as ReactMouseEvent } from "react";

interface BannerApi {
  about: string;
  created_at: string;
  id: number;
  photo_url: string;
  position: number;
  status: string;
  text: string;
  title: string;
}

const WEB_APP_BASE_URL = "https://menpoint.net";

const getCategoryChildren = (
  category: CatalogCategory,
): CatalogSubCategory[] => {
  const nested = category.sub_categories ?? category.children ?? [];
  return nested.filter((item) => item?.id && item?.name);
};

const ScrollToTopButton = ({ enabled }: { enabled: boolean }) => {
  const [isVisible, setIsVisible] = useState(false);
  const isVisibleRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      isVisibleRef.current = false;
      setIsVisible(false);
      return;
    }

    const scrollContainer = document.getElementById("root");
    if (!scrollContainer) return;

    let rafId = 0;

    const evaluateVisibility = () => {
      const currentTop = scrollContainer.scrollTop;
      const shouldShow = isVisibleRef.current
        ? currentTop > 260
        : currentTop > 380;

      if (shouldShow !== isVisibleRef.current) {
        isVisibleRef.current = shouldShow;
        setIsVisible(shouldShow);
      }
    };

    const handleScroll = () => {
      if (rafId) return;

      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        evaluateVisibility();
      });
    };

    evaluateVisibility();
    scrollContainer.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [enabled]);

  const handleScrollToTop = () => {
    const scrollContainer = document.getElementById("root");
    if (!scrollContainer) return;

    scrollContainer.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  if (!enabled) return null;

  return (
    <button
      className={`${styles.scrollTopButton} ${
        isVisible ? styles.scrollTopButtonVisible : ""
      }`}
      onClick={handleScrollToTop}
      aria-label="Наверх"
      type="button"
    >
      <span className={styles.scrollTopButtonArrow}>↑</span>
    </button>
  );
};

const CatalogPage = () => {
  const tg = window.Telegram?.WebApp;
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth > 500);
  const [categories, setCategories] = useState<CatalogCategory[]>([]);
  const [selectedCatalogCategoryId, setSelectedCatalogCategoryId] = useState<
    number | null
  >(null);
  const [selectedSubCategoryIds, setSelectedSubCategoryIds] = useState<
    number[]
  >([]);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<number[]>([]);
  const [isCategoryTreeOpen, setIsCategoryTreeOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("Все");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<CatalogItem | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [catalog, setCatalog] = useState<CatalogItem[]>([]);
  const [favoriteIds, setFavoriteIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadedBanners, setLoadedBanners] = useState<Set<number>>(new Set());
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [favoritesLoaded, setFavoritesLoaded] = useState(false);
  const [banners, setBanners] = useState<BannerApi[]>([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<CatalogItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [imageSlideDirection, setImageSlideDirection] = useState(1);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const imageLoadCache = useRef<Set<string>>(new Set());
  const loadingStartTime = useRef<number>(Date.now());
  const hasOpenedFromState = useRef(false);
  const categoryTreeRef = useRef<HTMLDivElement | null>(null);
  const setNotification = useNotificationStore(
    (state) => state.setNotification,
  );
  const { updateCartCount, cartItemsCount } = useCartStore();
  const { setVisibility: setNavMenuVisibility } = useNavMenuStore();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedCatalogCategory =
    categories.find((category) => category.id === selectedCatalogCategoryId) ??
    null;
  const selectedCatalogCategoryChildren = selectedCatalogCategory
    ? getCategoryChildren(selectedCatalogCategory)
    : [];
  const isAllSelectedInCategory =
    !!selectedCatalogCategory &&
    selectedCatalogCategoryChildren.length > 0 &&
    selectedSubCategoryIds.length === selectedCatalogCategoryChildren.length;
  const selectedCatalogSubCategoryIds =
    selectedCatalogCategory && isAllSelectedInCategory
      ? undefined
      : selectedSubCategoryIds.length > 0
        ? selectedSubCategoryIds
        : undefined;

  // Восстанавливаем NavMenu при размонтировании компонента
  useEffect(() => {
    return () => {
      setNavMenuVisibility(true);
    };
  }, [setNavMenuVisibility]);

  useEffect(() => {
    const onResize = () => {
      setIsDesktop(window.innerWidth > 500);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    if (isDesktop) {
      setNavMenuVisibility(false);
      return;
    }
    if (!isSearchMode) {
      setNavMenuVisibility(true);
    }
  }, [isDesktop, isSearchMode, setNavMenuVisibility]);

  useEffect(() => {
    if (isDesktop && isSearchMode) {
      setIsSearchMode(false);
      setSearchResults([]);
    }
  }, [isDesktop, isSearchMode]);

  useEffect(() => {
    if (!isCategoryTreeOpen) {
      return;
    }

    const handleClickOutside = (event: Event) => {
      if (
        categoryTreeRef.current &&
        event.target instanceof Node &&
        !categoryTreeRef.current.contains(event.target)
      ) {
        setIsCategoryTreeOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isCategoryTreeOpen]);

  const handleAddToCartClick = async () => {
    if (!selectedItem) return;
    if (!selectedSize) {
      setNotification({
        type: "Error",
        message: "Пожалуйста, выберите размер",
      });
      return;
    }

    try {
      const originalPrice = selectedItem.price.amount;
      const discountPercent =
        selectedItem.discount &&
        selectedItem.discount.type === "percentage" &&
        selectedItem.discount.value
          ? selectedItem.discount.value
          : 0;

      await addToCart(
        selectedItem.id,
        selectedSize,
        originalPrice,
        discountPercent,
      );
      setNotification({
        type: "Success",
        message: "Товар успешно добавлен в корзину",
      });
      setIsModalOpen(false);
      await updateCartCount();
    } catch (error) {
      console.error("Ошибка при добавлении в корзину:", error);
    }
  };

  const fetchCatalogProducts = async (
    category?: CatalogCategory | null,
    subCategoryIds?: number[],
  ) => {
    const response = await getCatalog(
      category
        ? {
            categoryId: category.id,
            subCategoryIds:
              subCategoryIds && subCategoryIds.length > 0
                ? subCategoryIds
                : undefined,
          }
        : undefined,
    );
    setCatalog(response.data);
  };

  const toggleCategoryExpansion = (categoryId: number) => {
    setExpandedCategoryIds((prev) =>
      prev.includes(categoryId) ? [] : [categoryId],
    );
  };

  const handleCatalogCategoryClick = async (category: CatalogCategory) => {
    const childIds = getCategoryChildren(category).map((item) => item.id);
    setSelectedCatalogCategoryId(category.id);
    setSelectedSubCategoryIds(childIds);
    setSearchResults([]);
    setExpandedCategoryIds(childIds.length > 0 ? [category.id] : []);
    setIsCategoryTreeOpen(childIds.length > 0);

    try {
      await fetchCatalogProducts(category);
    } catch (error) {
      console.error("Category filter error:", error);
    }
  };

  const handleCatalogSubCategoryClick = async (
    category: CatalogCategory,
    subCategoryId: number,
  ) => {
    const childIds = getCategoryChildren(category).map((item) => item.id);
    const isSameCategory = selectedCatalogCategoryId === category.id;
    const currentSelectedIds = isSameCategory ? selectedSubCategoryIds : [];
    const isAllSelected =
      currentSelectedIds.length > 0 &&
      currentSelectedIds.length === childIds.length;

    let nextSelectedIds = [subCategoryId];

    if (isSameCategory) {
      if (isAllSelected) {
        nextSelectedIds = [subCategoryId];
      } else if (currentSelectedIds.includes(subCategoryId)) {
        nextSelectedIds =
          currentSelectedIds.length > 1
            ? currentSelectedIds.filter((id) => id !== subCategoryId)
            : [subCategoryId];
      } else {
        nextSelectedIds = [...currentSelectedIds, subCategoryId];
      }
    }

    setSelectedCatalogCategoryId(category.id);
    setSelectedSubCategoryIds(nextSelectedIds);
    setSearchResults([]);
    setExpandedCategoryIds([category.id]);

    try {
      await fetchCatalogProducts(
        category,
        nextSelectedIds.length === childIds.length
          ? undefined
          : nextSelectedIds,
      );
    } catch (error) {
      console.error("Subcategory filter error:", error);
    }
  };

  const handleResetCatalogCategory = async () => {
    setSelectedCatalogCategoryId(null);
    setSelectedSubCategoryIds([]);
    setSearchResults([]);
    setExpandedCategoryIds([]);
    setIsCategoryTreeOpen(false);

    try {
      await fetchCatalogProducts();
    } catch (error) {
      console.error("Category reset error:", error);
    }
  };

  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        const response = await getCatalog();
        console.log("response:", response);
        getCategoriesTree()
          .then((categoriesResponse) => {
            setCategories(categoriesResponse.data || []);
          })
          .catch((error) => {
            console.error("Ошибка при получении категорий:", error);
            setCategories([]);
          });
        const bannersNew = await getBanners();
        console.log("bannersNew:", bannersNew);
        setCatalog(response.data);

        const bannersArray =
          (bannersNew as { data: BannerApi[] }).data ??
          (bannersNew as BannerApi[]);

        const activeBanners = bannersArray
          .filter((banner) => banner.status === "active")
          .sort((a, b) => a.position - b.position);

        setBanners(activeBanners);

        setCatalogLoaded(true);
      } catch (error) {
        console.error("Ошибка при получении каталога:", error);
        setCatalogLoaded(true);
      }
    };
    fetchCatalog();
  }, []);

  // Отслеживание загрузки изображений баннеров
  useEffect(() => {
    if (!banners.length) return;

    const loadBannerImage = (bannerId: number, src?: string): Promise<void> => {
      if (!src) {
        // Если нет картинки, помечаем баннер как загруженный
        setLoadedBanners((prev) => {
          const newSet = new Set(prev);
          newSet.add(bannerId);
          return newSet;
        });
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          setLoadedBanners((prev) => {
            const newSet = new Set(prev);
            newSet.add(bannerId);
            return newSet;
          });
          resolve();
        };
        img.onerror = () => {
          // Если изображение не загрузилось, все равно помечаем как загруженное
          setLoadedBanners((prev) => {
            const newSet = new Set(prev);
            newSet.add(bannerId);
            return newSet;
          });
          resolve();
        };
        img.src = src;
      });
    };

    const loadAllBanners = async () => {
      await Promise.all(
        banners.map((banner) => loadBannerImage(banner.id, banner.photo_url)),
      );
    };

    loadAllBanners();
  }, [banners]);

  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const favoritesResponse = await getFavorites();
        console.log("favoritesResponse:", favoritesResponse);
        const favoriteItems = favoritesResponse.data || [];
        const ids = favoriteItems.map((item: CatalogItem) => item.id);
        setFavoriteIds(ids);
        setFavoritesLoaded(true);
      } catch (error) {
        console.error("Ошибка при получении избранного:", error);
        setFavoritesLoaded(true);
      }
    };
    fetchFavorites();
  }, []);

  // Проверка готовности всех данных
  useEffect(() => {
    const allBannersLoaded =
      !banners.length ||
      banners.every((banner) => loadedBanners.has(banner.id));

    if (allBannersLoaded && catalogLoaded && favoritesLoaded) {
      const minLoadingTime = 2000; // 2 секунды
      const elapsedTime = Date.now() - loadingStartTime.current;
      const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

      if (remainingTime > 0) {
        setTimeout(() => {
          setIsLoading(false);
        }, remainingTime);
      } else {
        // Небольшая задержка для плавности, если уже прошло минимум времени
        setTimeout(() => {
          setIsLoading(false);
        }, 300);
      }
    }
  }, [banners, catalogLoaded, favoritesLoaded, loadedBanners]);

  useEffect(() => {
    if (hasOpenedFromState.current) return;
    const state = location.state as { openProductId?: number } | null;
    const stateProductId = state?.openProductId;

    const searchParams = new URLSearchParams(location.search);
    const openProductParam = searchParams.get("open_product");
    const startAppParam = searchParams.get("startapp");

    const openProductIdFromQuery = openProductParam
      ? Number(openProductParam)
      : NaN;
    const startAppMatch = startAppParam?.match(/^open_product-(\d+)$/);
    const openProductIdFromStartApp = startAppMatch
      ? Number(startAppMatch[1])
      : NaN;

    const openProductId =
      stateProductId ??
      (Number.isFinite(openProductIdFromQuery)
        ? openProductIdFromQuery
        : undefined) ??
      (Number.isFinite(openProductIdFromStartApp)
        ? openProductIdFromStartApp
        : undefined);

    if (!openProductId) return;

    if (!catalog.length) return;

    const item = catalog.find((product) => product.id === openProductId);
    if (!item) return;

    hasOpenedFromState.current = true;
    handleOpenItemModal(item);

    // очищаем state, чтобы при навигации назад модалка не открывалась снова
    navigate(location.pathname, { replace: true, state: {} });
  }, [catalog, location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, [isModalOpen]);

  const handleBannerClick = (banner: { id?: number | string }) => {
    if (!banner.id) return;

    const fullBanner = banners.find((b) => String(b.id) === String(banner.id));

    navigate(`/banner/${banner.id}`, { state: { banner: fullBanner } });
  };

  const formatPrice = (price: number): string => {
    return Math.round(price)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  };

  const calculateDiscountedPrice = (item: CatalogItem): number | null => {
    if (!item.discount || !item.discount.value) {
      return null;
    }

    const { type, value } = item.discount;

    if (type === "percentage") {
      const discountAmount = (item.price.amount * value) / 100;
      return item.price.amount - discountAmount;
    }

    if (type === "fixed") {
      return Math.max(0, item.price.amount - value);
    }

    return null;
  };

  const filteredItems = catalog.filter((item: CatalogItem) => {
    if ("status" in item && item.status !== "active") {
      return false;
    }
    if (selectedCategory === "Все") {
      return true;
    }
    if (selectedCategory === "Новинки") {
      return item.is_new === true;
    }
    if (selectedCategory === "Дискаунт") {
      return !!(
        item.discount &&
        item.discount.value &&
        item.discount.value !== 0
      );
    }
    return true;
  });

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const desktopItems =
    isDesktop && normalizedSearch
      ? filteredItems.filter((item) =>
          item.view_details.title.toLowerCase().includes(normalizedSearch),
        )
      : filteredItems;
  const selectedCategorySummary = selectedCatalogCategory
    ? selectedCatalogCategoryChildren.length === 0 || isAllSelectedInCategory
      ? selectedCatalogCategory.name
      : selectedSubCategoryIds.length === 1
        ? (selectedCatalogCategoryChildren.find(
            (item) => item.id === selectedSubCategoryIds[0],
          )?.name ?? selectedCatalogCategory.name)
        : `${selectedCatalogCategory.name} (${selectedSubCategoryIds.length})`
    : "Все товары";

  const preloadImage = (src?: string): Promise<void> => {
    if (!src) return Promise.resolve();
    if (imageLoadCache.current.has(src)) return Promise.resolve();

    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        imageLoadCache.current.add(src);
        resolve();
      };
      img.onerror = () => {
        resolve();
      };
      img.src = src;
      if (img.complete) {
        imageLoadCache.current.add(src);
        resolve();
      }
    });
  };

  const switchToImage = async (nextIndex: number, direction: number) => {
    if (!selectedItem) return;

    const images = selectedItem.view_details.images;
    if (!images[nextIndex] || nextIndex === currentImageIndex) return;

    await preloadImage(images[nextIndex]);
    setImageSlideDirection(direction);
    setCurrentImageIndex(nextIndex);
  };

  const showNextImage = () => {
    if (!selectedItem) return;
    const imagesLength = selectedItem.view_details.images.length;
    const nextIndex =
      currentImageIndex === imagesLength - 1 ? 0 : currentImageIndex + 1;
    void switchToImage(nextIndex, 1);
  };

  const showPreviousImage = () => {
    if (!selectedItem) return;
    const imagesLength = selectedItem.view_details.images.length;
    const nextIndex =
      currentImageIndex === 0 ? imagesLength - 1 : currentImageIndex - 1;
    void switchToImage(nextIndex, -1);
  };

  const handleSwipe = () => {
    if (
      touchStartX.current === null ||
      touchEndX.current === null ||
      !selectedItem
    ) {
      return;
    }

    const distance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (Math.abs(distance) > minSwipeDistance) {
      if (distance > 0) {
        showNextImage();
      } else {
        showPreviousImage();
      }
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  useEffect(() => {
    if (!selectedItem) return;

    const images = selectedItem.view_details.images;
    if (!images.length) return;

    void preloadImage(images[0]);
    void Promise.all(images.map((src) => preloadImage(src)));
  }, [selectedItem]);

  const handleOpenItemModal = (item: CatalogItem) => {
    setSelectedItem(item);
    setCurrentImageIndex(0);
    setSelectedSize(null);
    const isFavorite = favoriteIds.includes(item.id);
    setIsWishlisted(isFavorite);
    setIsModalOpen(true);
  };

  const buildProductDeepLink = (productId: number): string => {
    const url = new URL("catalog", `${WEB_APP_BASE_URL.replace(/\/+$/, "")}/`);
    url.searchParams.set("open_product", String(productId));
    return url.toString();
  };

  const handleCopyProductLink = (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!selectedItem) return;

    const link = buildProductDeepLink(selectedItem.id);

    const copyWithFallback = () => {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = link;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        setNotification({
          type: "Success",
          message: "Ссылка скопирована",
        });
      } catch (err) {
        console.error("Clipboard fallback failed:", err);
        setNotification({
          type: "Error",
          message: "Не удалось скопировать ссылку",
        });
      }
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(link)
        .then(() => {
          setNotification({
            type: "Success",
            message: "Ссылка скопирована",
          });
        })
        .catch(() => {
          copyWithFallback();
        });
    } else {
      copyWithFallback();
    }
  };

  const handleManagerContactClick = () => {
    const managerUrl = "https://t.me/menpoint1";
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(managerUrl);
      return;
    }
    window.open(managerUrl, "_blank", "noopener,noreferrer");
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className={styles.container}>
      {!isDesktop && <BackButton />}
      {!isSearchMode && !isDesktop && (
        <>
          <SupportQuick />
          <SearchQuick
            onSearchClick={() => {
              setIsSearchMode(true);
              setNavMenuVisibility(false);
            }}
          />
        </>
      )}

      <AnimatePresence mode="wait">
        {isSearchMode ? (
          <motion.div
            key="search"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div
              className={`${styles.searchContainer} ${
                !isDesktop ? styles.searchContainerMobileOffset : ""
              }`}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div className={styles.searchTitle} style={{ marginBottom: 0 }}>
                  Поиск
                </div>
                <button
                  className={styles.searchCloseButton}
                  style={{
                    position: "relative",
                    right: 0,
                    top: 0,
                    transform: "none",
                  }}
                  onClick={() => {
                    setIsSearchMode(false);
                    setSearchQuery("");
                    setSearchResults([]);
                    setNavMenuVisibility(true);
                  }}
                />
              </div>
              <div
                className={styles.searchBarWrapper}
                style={{ marginTop: "15px" }}
              >
                <input
                  type="text"
                  className={styles.searchBar}
                  placeholder="Введите название..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
              </div>
              {searchResults.length === 0 && (
                <div className={styles.searchHint}>
                  Воспользуйтесь поиском, чтобы найти конкретную вещь.
                </div>
              )}
            </div>

            {searchResults.length > 0 && (
              <div className={styles.catalogPageContent}>
                <div
                  className={styles.catalogPageItems}
                  style={{ paddingBottom: 70 }}
                >
                  {searchResults.map((item, index) => (
                    <motion.div
                      className={styles.catalogPageItem}
                      key={item.id}
                      onClick={() => handleOpenItemModal(item)}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{
                        duration: 0.2,
                        delay: Math.min(index, 5) * 0.03,
                      }}
                    >
                      <div className={styles.catalogPageItemImage}>
                        <img
                          src={item.view_details.images[0]}
                          alt={"Фото товара"}
                        />
                      </div>
                      <div className={styles.catalogPageItemTitle}>
                        {item.view_details.title}
                      </div>
                      <div className={styles.catalogPageItemPrice}>
                        {(() => {
                          const discountedPrice =
                            calculateDiscountedPrice(item);
                          return discountedPrice !== null ? (
                            <>
                              <span
                                className={styles.catalogPageItemDiscountPrice}
                              >
                                {formatPrice(discountedPrice)} ₽
                              </span>
                              <span
                                className={styles.catalogPageItemOriginalPrice}
                              >
                                {formatPrice(item.price.amount)} ₽
                              </span>
                            </>
                          ) : (
                            <span>{formatPrice(item.price.amount)} ₽</span>
                          );
                        })()}
                      </div>
                      <button className={styles.catalogPageItemButton}>
                        Выбрать
                      </button>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            <div className={styles.searchButtonContainer}>
              <button
                className={styles.searchButton}
                onClick={async () => {
                  if (!searchQuery.trim()) return;
                  setIsSearching(true);
                  try {
                    const response = await searchProducts(searchQuery, {
                      categoryId: selectedCatalogCategory?.id,
                      categorySlug: selectedCatalogCategory?.slug,
                      subCategoryIds: selectedCatalogSubCategoryIds,
                    });
                    const results = (response.data || []).slice(0, 50);
                    setSearchResults(results);
                    if (results.length === 0) {
                      setNotification({
                        type: "Warning",
                        message: "Ничего не найдено",
                      });
                    }
                  } catch (error) {
                    console.error("Ошибка поиска:", error);
                  } finally {
                    setIsSearching(false);
                  }
                }}
                disabled={isSearching}
              >
                {isSearching ? "Поиск..." : "Найти"}
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="catalog"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {isDesktop && (
              <div className={styles.desktopToolbar}>
                <div className={styles.desktopTopNav}>
                  <button
                    className={`${styles.desktopTopNavButton} ${styles.desktopTopNavButtonActive} ${styles.desktopTopNavIconOnlyButton}`}
                    onClick={() => navigate("/catalog")}
                    aria-label="Catalog"
                  >
                    <CatalogNavIcon className={styles.desktopTopNavIcon} />
                  </button>
                  <button
                    className={`${styles.desktopTopNavButton} ${styles.desktopTopNavIconOnlyButton}`}
                    onClick={() => navigate("/saved")}
                    aria-label="Saved"
                  >
                    <HeartNavIcon className={styles.desktopTopNavIcon} />
                  </button>
                  <button
                    className={`${styles.desktopTopNavButton} ${styles.desktopTopNavIconOnlyButton}`}
                    onClick={handleManagerContactClick}
                    aria-label="Manager"
                  >
                    <SupportNavIcon className={styles.desktopTopNavIcon} />
                  </button>
                  <button
                    className={`${styles.desktopTopNavButton} ${styles.desktopTopNavIconOnlyButton}`}
                    onClick={() => navigate("/bucket")}
                    aria-label="Cart"
                  >
                    <BucketNavIcon className={styles.desktopTopNavIcon} />
                    {cartItemsCount > 0 && (
                      <span className={styles.desktopTopNavBadge}>
                        {cartItemsCount}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            )}
            <div className={styles.carouselTopOffset}>
              <Carousel
                banners={banners.map((banner) => ({
                  id: banner.id,
                  title: banner.title,
                  description: banner.about,
                  background: banner.photo_url,
                }))}
                onBannerClick={handleBannerClick}
              />
            </div>
            <div className={styles.catalogPageContent}>
              <div className={styles.catalogPageTitle}>Выбери свой стиль</div>
              <div
                className={`${styles.catalogPageControlsRow} ${
                  isDesktop ? styles.catalogPageControlsRowDesktop : ""
                }`}
              >
                {isDesktop && (
                  <div className={styles.desktopSearchWrap}>
                    <input
                      type="text"
                      className={styles.desktopSearchInput}
                      placeholder="Поиск по каталогу..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                )}
                {categories.length > 0 && (
                  <div
                    className={styles.catalogPageCategoryTree}
                    ref={categoryTreeRef}
                  >
                    <button
                      type="button"
                      className={`${styles.catalogPageCategorySelect} ${
                        isCategoryTreeOpen
                          ? styles.catalogPageCategorySelectOpen
                          : ""
                      }`}
                      onClick={() => {
                        if (!isCategoryTreeOpen && selectedCatalogCategory) {
                          const children = getCategoryChildren(
                            selectedCatalogCategory,
                          );
                          setExpandedCategoryIds(
                            children.length > 0
                              ? [selectedCatalogCategory.id]
                              : [],
                          );
                        }
                        setIsCategoryTreeOpen((prev) => !prev);
                      }}
                      aria-expanded={isCategoryTreeOpen}
                      aria-label="Выбрать категорию"
                    >
                      <span className={styles.catalogPageCategorySelectLabel}>
                        Категории
                      </span>
                      <span className={styles.catalogPageCategorySelectValue}>
                        {selectedCategorySummary}
                      </span>
                    </button>
                    {isCategoryTreeOpen && (
                      <div className={styles.catalogPageCategoryDropdown}>
                        <div className={styles.catalogPageCategoryTreeHeader}>
                          <span className={styles.catalogPageCategoryTreeLabel}>
                            Категории
                          </span>
                          <button
                            type="button"
                            className={`${styles.catalogPageCategoryReset} ${
                              !selectedCatalogCategoryId
                                ? styles.catalogPageCategoryResetActive
                                : ""
                            }`}
                            onClick={() => void handleResetCatalogCategory()}
                          >
                            Сбросить
                          </button>
                        </div>
                        <div className={styles.catalogPageCategoryTreeList}>
                          {categories.map((category) => {
                            const children = getCategoryChildren(category);
                            const isExpanded = expandedCategoryIds.includes(
                              category.id,
                            );
                            const isActiveCategory =
                              selectedCatalogCategoryId === category.id;

                            return (
                              <div
                                key={category.id}
                                className={`${styles.catalogPageCategoryTreeItem} ${
                                  isActiveCategory
                                    ? styles.catalogPageCategoryTreeItemActive
                                    : ""
                                }`}
                              >
                                <div
                                  className={styles.catalogPageCategoryTreeRow}
                                >
                                  <button
                                    type="button"
                                    className={`${styles.catalogPageCategoryTreeButton} ${
                                      isActiveCategory
                                        ? styles.catalogPageCategoryTreeButtonActive
                                        : ""
                                    }`}
                                    onClick={() =>
                                      void handleCatalogCategoryClick(category)
                                    }
                                  >
                                    <span
                                      className={
                                        styles.catalogPageCategoryTreeButtonTitle
                                      }
                                    >
                                      {category.name}
                                    </span>
                                    {children.length > 0 && (
                                      <span
                                        className={
                                          styles.catalogPageCategoryTreeButtonMeta
                                        }
                                      >
                                        {selectedCatalogCategoryId ===
                                          category.id &&
                                        selectedSubCategoryIds.length === 1
                                          ? "1 подкатегория"
                                          : `${children.length} подкатегорий`}
                                      </span>
                                    )}
                                  </button>
                                  {children.length > 0 && (
                                    <button
                                      type="button"
                                      className={`${styles.catalogPageCategoryExpand} ${
                                        isExpanded
                                          ? styles.catalogPageCategoryExpandOpen
                                          : ""
                                      }`}
                                      onClick={() =>
                                        toggleCategoryExpansion(category.id)
                                      }
                                      aria-label={
                                        isExpanded
                                          ? "Скрыть подкатегории"
                                          : "Показать подкатегории"
                                      }
                                    >
                                      <span />
                                    </button>
                                  )}
                                </div>
                                {children.length > 0 && isExpanded && (
                                  <div
                                    className={
                                      styles.catalogPageSubCategoryList
                                    }
                                  >
                                    {children.map((subCategory) => {
                                      const isChecked =
                                        selectedCatalogCategoryId ===
                                          category.id &&
                                        selectedSubCategoryIds.includes(
                                          subCategory.id,
                                        );
                                      const isSingleChecked =
                                        selectedCatalogCategoryId ===
                                          category.id &&
                                        selectedSubCategoryIds.length === 1 &&
                                        selectedSubCategoryIds[0] ===
                                          subCategory.id;

                                      return (
                                        <button
                                          key={subCategory.id}
                                          type="button"
                                          className={`${styles.catalogPageSubCategoryItem} ${
                                            isSingleChecked
                                              ? styles.catalogPageSubCategoryItemActive
                                              : ""
                                          }`}
                                          onClick={() =>
                                            void handleCatalogSubCategoryClick(
                                              category,
                                              subCategory.id,
                                            )
                                          }
                                        >
                                          <span
                                            className={`${styles.catalogPageSubCategoryCheckbox} ${
                                              isChecked
                                                ? styles.catalogPageSubCategoryCheckboxChecked
                                                : ""
                                            }`}
                                          />
                                          <span
                                            className={
                                              styles.catalogPageSubCategoryName
                                            }
                                          >
                                            {subCategory.name}
                                          </span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div
                  className={`${styles.catalogPageCategories} ${
                    isDesktop ? styles.catalogPageCategoriesDesktop : ""
                  }`}
                >
                  <div
                    className={`${styles.catalogPageCategory} ${
                      selectedCategory === "Все"
                        ? styles.catalogPageCategoryActive
                        : ""
                    }`}
                    onClick={() => setSelectedCategory("Все")}
                  >
                    Все
                  </div>
                  <div
                    className={`${styles.catalogPageCategory} ${
                      selectedCategory === "Новинки"
                        ? styles.catalogPageCategoryActive
                        : ""
                    }`}
                    onClick={() => setSelectedCategory("Новинки")}
                  >
                    Новинки
                  </div>
                  <div
                    className={`${styles.catalogPageCategory} ${
                      selectedCategory === "Дискаунт"
                        ? styles.catalogPageCategoryActive
                        : ""
                    }`}
                    onClick={() => setSelectedCategory("Дискаунт")}
                  >
                    Дискаунт
                  </div>
                </div>
              </div>
              <div className={styles.catalogPageItems}>
                {desktopItems.length === 0 ? (
                  <div className={styles.noItems}>
                    Нет предметов, удовлетворяющих ваш запрос
                  </div>
                ) : (
                  <AnimatePresence mode="wait">
                    {desktopItems.map((item, index) => (
                      <motion.div
                        className={styles.catalogPageItem}
                        key={item.id}
                        onClick={() => handleOpenItemModal(item)}
                        initial={{ opacity: 0, scale: 0.8, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.8, y: -20 }}
                        transition={{
                          duration: 0.3,
                          delay: Math.min(index, 5) * 0.05,
                          ease: [0.25, 0.46, 0.45, 0.94],
                        }}
                      >
                        <div className={styles.catalogPageItemImage}>
                          <img
                            src={item.view_details.images[0]}
                            alt={"Фото товара"}
                            loading="lazy"
                          />
                        </div>
                        <div className={styles.catalogPageItemTitle}>
                          {item.view_details.title}
                        </div>
                        <div className={styles.catalogPageItemPrice}>
                          {(() => {
                            const discountedPrice =
                              calculateDiscountedPrice(item);
                            return discountedPrice !== null ? (
                              <>
                                <span
                                  className={
                                    styles.catalogPageItemDiscountPrice
                                  }
                                >
                                  {formatPrice(discountedPrice)} ₽
                                </span>
                                <span
                                  className={
                                    styles.catalogPageItemOriginalPrice
                                  }
                                >
                                  {formatPrice(item.price.amount)} ₽
                                </span>
                              </>
                            ) : (
                              <span>{formatPrice(item.price.amount)} ₽</span>
                            );
                          })()}
                        </div>
                        <button className={styles.catalogPageItemButton}>
                          Выбрать
                        </button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <ScrollToTopButton enabled={isDesktop && !isModalOpen} />

      {/* Модальное окно */}
      <AnimatePresence>
        {isModalOpen && selectedItem && (
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              className={styles.modalContent}
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={styles.modalImageContainer}
                onTouchStart={(e) => {
                  touchStartX.current = e.touches[0].clientX;
                }}
                onTouchEnd={(e) => {
                  touchEndX.current = e.changedTouches[0].clientX;
                  handleSwipe();
                }}
                onMouseDown={(e) => {
                  touchStartX.current = e.clientX;
                }}
                onMouseUp={(e) => {
                  touchEndX.current = e.clientX;
                  handleSwipe();
                }}
              >
                <button
                  className={styles.modalCloseButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsModalOpen(false);
                  }}
                  aria-label="Закрыть"
                >
                  ×
                </button>
                {selectedItem.view_details.images.length > 1 && (
                  <>
                    <button
                      className={`${styles.modalImageArrow} ${styles.modalImageArrowLeft}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        showPreviousImage();
                      }}
                      aria-label="Previous image"
                    >
                      ‹
                    </button>
                    <button
                      className={`${styles.modalImageArrow} ${styles.modalImageArrowRight}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        showNextImage();
                      }}
                      aria-label="Next image"
                    >
                      ›
                    </button>
                  </>
                )}
                <motion.img
                  key={`${selectedItem.id}-${currentImageIndex}`}
                  src={selectedItem.view_details.images[currentImageIndex]}
                  alt={selectedItem.view_details.title}
                  className={styles.modalImage}
                  initial={{
                    opacity: 0,
                    x: imageSlideDirection > 0 ? 28 : -28,
                  }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{
                    opacity: 0,
                    x: imageSlideDirection > 0 ? -28 : 28,
                  }}
                  transition={{ duration: 0.3 }}
                />
              </div>
              {selectedItem.view_details.images.length > 1 && (
                <div className={styles.modalDots}>
                  {selectedItem.view_details.images.map((_, index) => (
                    <button
                      key={index}
                      className={`${styles.modalDot} ${
                        currentImageIndex === index ? styles.modalDotActive : ""
                      }`}
                      onClick={() => {
                        const direction = index > currentImageIndex ? 1 : -1;
                        void switchToImage(index, direction);
                      }}
                      aria-label={`Перейти к изображению ${index + 1}`}
                    />
                  ))}
                </div>
              )}

              <div className={styles.modalDetailsColumn}>
                <div className={styles.modalItemTitleRow}>
                  <div className={styles.modalItemTitle}>
                    {selectedItem.view_details.title}
                  </div>
                  {isDesktop && (
                    <button
                      className={styles.modalTitleCloseButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsModalOpen(false);
                      }}
                      aria-label="Закрыть"
                    >
                      <span aria-hidden="true">&#215;</span>
                    </button>
                  )}
                  {!isDesktop && (
                    <button
                      className={styles.modalItemButtonCopy}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!selectedItem) return;

                        const link = buildProductDeepLink(selectedItem.id);

                        const copyWithFallback = () => {
                          try {
                            const textarea = document.createElement("textarea");
                            textarea.value = link;
                            textarea.style.position = "fixed";
                            textarea.style.left = "-9999px";
                            document.body.appendChild(textarea);
                            textarea.select();
                            document.execCommand("copy");
                            document.body.removeChild(textarea);
                            setNotification({
                              type: "Success",
                              message: "Ссылка скопирована",
                            });
                          } catch (err) {
                            console.error("Clipboard fallback failed:", err);
                            setNotification({
                              type: "Error",
                              message: "Не удалось скопировать ссылку",
                            });
                          }
                        };

                        if (
                          navigator.clipboard &&
                          navigator.clipboard.writeText
                        ) {
                          navigator.clipboard
                            .writeText(link)
                            .then(() => {
                              setNotification({
                                type: "Success",
                                message: "Ссылка скопирована",
                              });
                            })
                            .catch(() => {
                              copyWithFallback();
                            });
                        } else {
                          copyWithFallback();
                        }
                      }}
                    >
                      <img
                        src={copyButtonIcon}
                        alt="Скопировать ссылку"
                        className={styles.modalItemCopyIcon}
                      />
                    </button>
                  )}
                </div>
                <div className={styles.modalItemDescription}>
                  {selectedItem.view_details.description}
                </div>
                <div className={styles.modalItemSizes}>Выберите размер</div>
                <div className={styles.modalItemSizesList}>
                  {selectedItem.sizes.map((size: string) => (
                    <div
                      className={`${styles.modalItemSize} ${
                        selectedSize === size ? styles.modalItemSizeActive : ""
                      }`}
                      key={size}
                      onClick={() => {
                        setSelectedSize(size);
                        console.log("Размер выбранный:", size);
                      }}
                    >
                      {size}
                    </div>
                  ))}
                </div>
                <div className={styles.modalItemPriceRow}>
                  <div className={styles.modalItemPrice}>
                    {(() => {
                      const discountedPrice =
                        calculateDiscountedPrice(selectedItem);
                      return discountedPrice !== null ? (
                        <>
                          <span className={styles.catalogPageItemDiscountPrice}>
                            {formatPrice(discountedPrice)}₽
                          </span>{" "}
                          <span className={styles.catalogPageItemOriginalPrice}>
                            {formatPrice(selectedItem.price.amount)}₽
                          </span>
                        </>
                      ) : (
                        <span className={styles.catalogPageItemPrice}>
                          {formatPrice(selectedItem.price.amount)}₽
                        </span>
                      );
                    })()}
                  </div>
                  {isDesktop && (
                    <button
                      className={`${styles.modalItemButtonCopy} ${styles.modalItemButtonCopyDesktop}`}
                      onClick={handleCopyProductLink}
                      aria-label="Скопировать ссылку"
                    >
                      <img
                        src={copyButtonIcon}
                        alt="Скопировать ссылку"
                        className={styles.modalItemCopyIcon}
                      />
                    </button>
                  )}
                </div>
                <div className={styles.modalItemButtons}>
                  <button
                    className={styles.modalItemButtonAddToCart}
                    onClick={handleAddToCartClick}
                  >
                    Добавить в корзину
                  </button>
                  <div
                    className={styles.modalItemButtonAddToWishlist}
                    onClick={async () => {
                      if (!selectedItem) return;

                      try {
                        if (isWishlisted) {
                          await removeFromFavorites(selectedItem.id);
                          setFavoriteIds((prev) =>
                            prev.filter((id) => id !== selectedItem.id),
                          );
                          setIsWishlisted(false);
                        } else {
                          await addToFavorites(selectedItem.id);
                          setFavoriteIds((prev) => [...prev, selectedItem.id]);
                          setIsWishlisted(true);
                        }
                      } catch (error) {
                        console.error(
                          "Ошибка при изменении избранного:",
                          error,
                        );
                      }
                    }}
                  >
                    <motion.div
                      animate={{ scale: isWishlisted ? [1, 1.2, 1] : 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <HeartIcon
                        className={styles.modalItemHeartIcon}
                        style={{
                          fill: isWishlisted ? "#ff0000" : "transparent",
                          stroke: isWishlisted ? "#ff0000" : "#818181",
                        }}
                      />
                    </motion.div>
                    <span className={styles.modalItemWishlistText}>
                      Сохранить
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CatalogPage;
